import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { Button } from 'components/Button/Button'
import Loader from 'components/Loader/Loader'
import Title from 'components/Title/Title'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PROGRESS_TYPES } from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'

import AddFacilityModal from './AddFacilityModal/AddFacilityModal'
import { IFacilityInfo } from './FacilityTable/FacilityItem/FacilityItem'
import FacilityTable from './FacilityTable/FacilityTable'

import style from './FacilityList.module.css'

const PAGE_SIZE = 20

const FacilityList = () => {
  const { t } = useTranslation()
  const [facilities, setFacilities] = useState<IFacilityInfo[]>([])
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [creatingProgress, setCreatingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [addFacilityModalOpen, setAddFacilityModalOpen] = useState(false)
  const [robots, setRobots] = useState<IRobotInfo[]>([])

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const fetchData = useCallback(async () => {
    setLoadingProgress(PROGRESS_TYPES.WORK)
    try {
      const response = await MainApi.fetchFacilities(page, PAGE_SIZE, searchText || undefined)
      setFacilities(response.results)
      setTotalPages(Math.max(1, Math.ceil(response.count / PAGE_SIZE)))
      setLoadingProgress(PROGRESS_TYPES.SUCCESS)
    } catch (error) {
      console.error('Error fetching facilities:', error)
      setLoadingProgress(PROGRESS_TYPES.ERROR)
    }
  }, [page, searchText])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const loadRobots = async () => {
      try {
        const result = await RobotApi.fetchRobots()
        setRobots(result.results)
      } catch (error) {
        console.error('Error fetching robots:', error)
      }
    }
    loadRobots()
  }, [])

  const onSearchChange = (value: string) => {
    setSearchText(value)
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1)
    }, 300)
  }

  const createFacility = async (data: {
    name: string
    facility_type: string
    street_address?: string
    city?: string
    country?: string
    robots_ids?: number[]
  }) => {
    setCreatingProgress(PROGRESS_TYPES.WORK)
    try {
      await MainApi.createFacility(data)
      setAddFacilityModalOpen(false)
      setCreatingProgress(PROGRESS_TYPES.SUCCESS)
      fetchData()
    } catch (error) {
      console.error('Error creating facility:', error)
      setCreatingProgress(PROGRESS_TYPES.ERROR)
    }
  }

  return (
    <div className={style.facilities}>
      <div className={style.titleBlock}>
        <Title text="Facilities" />
        <Button.Standart
          onClick={() => setAddFacilityModalOpen(true)}
          loading={creatingProgress === PROGRESS_TYPES.WORK}
        >
          Add Facility
        </Button.Standart>
      </div>
      <div className={style.controlsBlock}>
        <input
          className={style.searchInput}
          value={searchText}
          onChange={e => onSearchChange(e.target.value)}
          placeholder={t('common.search')}
        />
        <div className={style.paginationControlsTop}>
          <button
            className={style.paginationButton}
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            ←
          </button>
          <div className={style.pageInfo}>
            <input
              type="number"
              className={style.pageInput}
              value={page}
              min={1}
              max={totalPages}
              onChange={e => {
                const val = parseInt(e.target.value)
                if (!isNaN(val) && val >= 1 && val <= totalPages) {
                  setPage(val)
                }
              }}
            />
            / {totalPages}
          </div>
          <button
            className={style.paginationButton}
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          >
            →
          </button>
        </div>
      </div>
      {loadingProgress === PROGRESS_TYPES.WORK && <Loader />}
      {loadingProgress === PROGRESS_TYPES.SUCCESS && (
        <div className={style.tableContainer}>
          <FacilityTable facilities={facilities} onFacilitiesUpdated={fetchData} robots={robots} />
        </div>
      )}
      <AddFacilityModal
        isOpen={addFacilityModalOpen}
        onClose={() => setAddFacilityModalOpen(false)}
        onSave={createFacility}
        robots={robots}
      />
    </div>
  )
}

export default FacilityList
